import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  AutoComplete,
  Button,
  Card,
  Input,
  message,
  Pagination,
  Skeleton,
  Space,
  Tag,
  Typography,
} from "antd";
import {
  ArrowRightOutlined,
  BankOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  CloseOutlined,
  EnvironmentOutlined,
  FileDoneOutlined,
  FileProtectOutlined,
  GlobalOutlined,
  LinkOutlined,
  PlusOutlined,
  SearchOutlined,
  SolutionOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import { useWorkspace } from "contexts/WorkspaceContext";
import JobDetailsDrawer from "components/jobs/JobDetailsDrawer";
import SidePromotionSection from "components/promotions/SidePromotionSection";
import useAppConfig from "hooks/access-control/useAppConfig";
import paths from "routes/paths";
import { olreadiaService } from "services/olreadiaService";
import { ensureGoogleMapsPlacesLoaded } from "utils/googleMapsLoader";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import { OLIVEEB_COLORS } from "workspaces/colors";
import { JobCard } from "./JobSearch";
import { TalentProfileDrawer } from "./TalentSearch";
import "./WelcomeHomePage.css";

const { Paragraph, Text, Title } = Typography;
const RESULTS_PAGE_SIZE = 6;

const titleCase = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const pageContent = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.content)) return data.content;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.jobOpportunities)) return data.jobOpportunities;
  if (Array.isArray(data?.opportunities)) return data.opportunities;
  return [];
};

const pageTotalElements = (data) => {
  if (typeof data?.totalElements === "number") return data.totalElements;
  if (typeof data?.total === "number") return data.total;
  if (typeof data?.totalCount === "number") return data.totalCount;
  return pageContent(data).length;
};

const renderedPageTotal = (data, items, page) => {
  const apiTotal = pageTotalElements(data);
  const pageOffset = Math.max(page - 1, 0) * RESULTS_PAGE_SIZE;
  const renderedCount = items.length;

  if (renderedCount < RESULTS_PAGE_SIZE) return pageOffset + renderedCount;
  return Math.max(apiTotal, pageOffset + renderedCount);
};

const firstText = (...values) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim();
    if (typeof value === "number") return String(value);
  }
  return "";
};

const namedRefLabel = (value) => {
  if (!value) return "";
  if (typeof value === "string") return titleCase(value);
  return firstText(value.name, value.label, value.title, value.code);
};

const toList = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (value instanceof Set) return Array.from(value);
  return [value];
};

const normalizeStrings = (value) =>
  toList(value)
    .map((item) => (item && typeof item === "object" ? namedRefLabel(item) : titleCase(item)))
    .filter(Boolean);

const locationLabel = (locationItem) => {
  if (!locationItem) return "";
  if (typeof locationItem === "string") return locationItem;
  return firstText(
    locationItem.label,
    locationItem.name,
    locationItem.address,
    locationItem.formattedAddress,
    [locationItem.city, locationItem.state, locationItem.country].filter(Boolean).join(", "),
  );
};

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return String(value);
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

const jobCardFromApi = (job) => {
  const specializations = normalizeStrings(job?.specializations || job?.requiredSpecializations);
  const skills = uniqueSuggestionValues([
    ...normalizeStrings(job?.skills || job?.requiredSkills),
    ...normalizeStrings(job?.otherRequiredSkills),
    ...normalizeStrings(job?.requiredWorkAreas),
    ...normalizeStrings(job?.otherRequiredWorkAreas),
  ]);
  const locations = toList(job?.requiredLocations).map(locationLabel).filter(Boolean);
  const postedSource = job?.createdAt || job?.postedAt || job?.postStartDate;
  return {
    id: job?.id || job?.jobOpportunityId,
    title: job?.title || "Untitled job post",
    organization: namedRefLabel(job?.workspace) || namedRefLabel(job?.poster) || titleCase(job?.posterType) || "Employer post",
    location: locations[0] || titleCase(job?.locationType) || "",
    type: normalizeStrings(job?.workSchedules)[0] || titleCase(job?.workSchedule) || titleCase(job?.status || "Open"),
    badge: titleCase(job?.status || "Open"),
    postedDate: postedSource ? formatDate(postedSource) : "",
    specialization: specializations[0] || normalizeStrings(job?.requiredWorkAreas)[0] || "",
    summary: firstText(job?.summary, job?.description, job?.responsibilities, job?.requirements),
    skills: uniqueSuggestionValues([...specializations, ...skills]),
    raw: job,
  };
};

const talentCardFromApi = (profile) => {
  const profileId = profile?.professionProfileId || profile?.profileId || profile?.id;
  const skills = uniqueSuggestionValues([
    ...normalizeStrings(profile?.skills || profile?.interestedServices),
    ...normalizeStrings(profile?.specializations || profile?.sectors),
  ]);
  const locations = normalizeStrings(profile?.locations || profile?.interestedLocations);
  return {
    id: profileId,
    title: firstText(profile?.displayName, profile?.name, profile?.fullName, profile?.profileName, profile?.title) || "Talent profile",
    organization: firstText(profile?.professionTitle, profile?.headline, profile?.profileTitle) || "Professional profile",
    location: locations[0] || firstText(profile?.location, profile?.city, profile?.state),
    type: profile?.availableNow ? "Available now" : titleCase(profile?.educationLevel) || "Open to opportunities",
    badge: profile?.isVerified ? "Identity verified" : profile?.isBackgroundChecked ? "Background checked" : "Profile",
    summary: firstText(profile?.profileSummary, profile?.summary, profile?.description, profile?.experience),
    skills,
  };
};

const userName = (user) => {
  const name = [user?.firstName, user?.middleName, user?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  return name || user?.name || user?.username || user?.email || "there";
};

const photoUrls = {
  candidate:
    "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=900&q=80",
  profileTrust: "/welcome2.png",
  profile:
    "https://images.unsplash.com/photo-1497366754035-f200968a6e72?auto=format&fit=crop&w=900&q=80",
  hiring:
    "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=900&q=80",
  interviews:
    "https://images.unsplash.com/photo-1551836022-d5d88e9218df?auto=format&fit=crop&w=900&q=80",
};

const heroStyle = {
  position: "relative",
  overflow: "hidden",
  border: `1px solid ${OLIVEEB_COLORS.oliveebGreenSoft}`,
  borderRadius: 8,
  padding: 24,
  background: `linear-gradient(135deg, #ffffff 0%, ${OLIVEEB_COLORS.bgSoft} 48%, ${OLIVEEB_COLORS.oliveebBlueSoft} 100%)`,
  boxShadow: "0 18px 44px rgba(0, 64, 56, 0.10)",
};

const shineStyle = {
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  height: 3,
  background: `linear-gradient(90deg, ${OLIVEEB_COLORS.oliveebGreen}, ${OLIVEEB_COLORS.oliveebBlue}, ${OLIVEEB_COLORS.oliveebOrange})`,
};

const fieldStyle = {
  borderRadius: 8,
};

const actionButtonStyle = {
  borderRadius: 8,
  fontWeight: 800,
};

const homeCopyFor = ({ isHiringWorkspace, isOrganizationWorkspace }) => {
  if (isHiringWorkspace || isOrganizationWorkspace) {
    return {
      title: "Manage hiring with clearer next steps",
      subtitle:
        "Search talent, review applications, schedule interviews, and keep job posts moving from one Olreadia workspace.",
      searchTitle: "Find candidates",
      keywordPlaceholder: "Search by role, skill, or candidate keyword",
      locationPlaceholder: "Location or remote",
      primarySearchLabel: "Search talent",
      primaryPath: paths.talentSearch,
      secondaryPath: paths.jobPosts,
      secondaryLabel: "View job posts",
      createPath: paths.newJobPost,
      createLabel: "Create job post",
      modeLabel: "Hiring workspace",
    };
  }

  return {
    title: "Find roles and move faster when teams respond",
    subtitle:
      "Search jobs, keep your professional profile ready, request background checks, and manage applications in one place.",
    searchTitle: "Search jobs",
    keywordPlaceholder: "Job title, skill, or company",
    locationPlaceholder: "City, state, or remote",
    primarySearchLabel: "Search jobs",
    primaryPath: paths.jobSearch,
    secondaryPath: paths.jobApplications,
    secondaryLabel: "My applications",
    createPath: paths.professionalProfiles,
    createLabel: "Improve profile",
    modeLabel: "My job search",
  };
};

const promoCardsFor = ({ isHiringWorkspace, isOrganizationWorkspace, navigate }) => {
  if (isHiringWorkspace || isOrganizationWorkspace) {
    return [
      {
        title: "Post jobs that are easier to act on",
        text: "Share requirements, application questions, pay expectations, and next steps in a structured flow.",
        image: photoUrls.hiring,
        icon: <BankOutlined />,
        action: "Create job",
        onClick: () => navigate(paths.newJobPost),
      },
      {
        title: "Keep interviews and invitations moving",
        text: "Track candidate progress, interview sessions, invitations, and hiring updates without losing context.",
        image: photoUrls.interviews,
        icon: <CalendarOutlined />,
        action: "Open interviews",
        onClick: () => navigate(paths.interviews),
      },
      {
        title: "Build your organization presence",
        text: "Create a website for your workspace and keep your hiring brand visible to candidates.",
        image: photoUrls.profile,
        icon: <GlobalOutlined />,
        action: "Manage website",
        onClick: () => navigate(paths.website),
      },
    ];
  }

  return [
    {
      title: "Complete a profile employers can trust",
      text: "Keep your skills, services, locations, files, and profile snapshot ready before you apply.",
      image: photoUrls.profileTrust,
      icon: <UserOutlined />,
      action: "Open profiles",
      onClick: () => navigate(paths.professionalProfiles),
    },
    {
      title: "Shorten delays before a new role starts",
      text: "Request background checks early so teams can move faster after they choose you.",
      image: photoUrls.profile,
      icon: <FileProtectOutlined />,
      action: "Request check",
      onClick: () => navigate(paths.backgroundChecks),
    },
    {
      title: "Stay ready for every application",
      text: "Review applications, interviews, questions, status changes, and next steps from your workspace.",
      image: photoUrls.interviews,
      icon: <SolutionOutlined />,
      action: "My applications",
      onClick: () => navigate(paths.jobApplications),
    },
  ];
};

const quickActionsFor = ({ isHiringWorkspace, isOrganizationWorkspace }) => {
  if (isHiringWorkspace || isOrganizationWorkspace) {
    return [
      { label: "Talent Search", icon: <SearchOutlined />, to: paths.talentSearch },
      { label: "Applications", icon: <SolutionOutlined />, to: paths.jobApplications },
      { label: "Interviews", icon: <CalendarOutlined />, to: paths.interviews },
      { label: "Reports", icon: <CheckCircleOutlined />, to: paths.reports },
    ];
  }

  return [
    { label: "Job Search", icon: <SearchOutlined />, to: paths.jobSearch },
    { label: "Applications", icon: <SolutionOutlined />, to: paths.jobApplications },
    { label: "Profiles", icon: <UserOutlined />, to: paths.professionalProfiles },
    { label: "Background Checks", icon: <FileProtectOutlined />, to: paths.backgroundChecks },
  ];
};

const searchSuggestionsFor = ({ isHiringWorkspace, isOrganizationWorkspace }) => {
  const suggestions = isHiringWorkspace || isOrganizationWorkspace
    ? [
        "Registered nurse",
        "Home care aide",
        "Caregiver",
        "Medical assistant",
        "Customer support",
        "Project coordinator",
        "Background checked candidates",
        "Available this week",
      ]
    : [
        "Registered nurse",
        "Caregiver jobs",
        "Administrative assistant",
        "Customer support",
        "Remote roles",
        "Entry level",
        "Healthcare jobs",
        "Background check friendly",
      ];
  return suggestions.map((value) => ({ value, label: value }));
};

const roleOptionsFor = (roles, query) => {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return [];

  const seenRoleTitles = new Set();

  return roles
    .map((role, index) => {
      const title = String(role.title || "").toLowerCase();
      const synonyms = Array.isArray(role.synonyms)
        ? role.synonyms.map((value) => String(value || "").toLowerCase())
        : [];
      const description = String(role.description || "").toLowerCase();
      let rank = Number.POSITIVE_INFINITY;

      if (title === normalizedQuery) rank = 0;
      else if (title.startsWith(normalizedQuery)) rank = 1;
      else if (title.includes(normalizedQuery)) rank = 2;
      else if (synonyms.some((value) => value === normalizedQuery)) rank = 3;
      else if (synonyms.some((value) => value.startsWith(normalizedQuery))) rank = 4;
      else if (synonyms.some((value) => value.includes(normalizedQuery))) rank = 5;
      else if (description.includes(normalizedQuery)) rank = 6;

      return { role, index, rank };
    })
    .filter((item) => Number.isFinite(item.rank))
    .sort(
      (a, b) =>
        a.rank - b.rank ||
        String(a.role.title || "").localeCompare(String(b.role.title || "")) ||
        a.index - b.index,
    )
    .filter(({ role }) => {
      const title = String(role.title || "").trim().replace(/\s+/g, " ");
      const key = title.toLowerCase();
      if (!title || seenRoleTitles.has(key)) return false;
      seenRoleTitles.add(key);
      return true;
    })
    .slice(0, 8)
    .map(({ role }) => ({
      value: role.title,
      label: (
        <Space>
          <SolutionOutlined style={{ color: OLIVEEB_COLORS.oliveebGreen }} />
          <span>{role.title}</span>
        </Space>
      ),
    }));
};

const uniqueSuggestionValues = (values) =>
  Array.from(new Set(values.map((value) => String(value || "").trim().replace(/\s+/g, " ")).filter(Boolean)));

const popularSearchLabel = (item) => String(item?.label || item?.searchQuery || "").trim();

const popularLocationLabel = (item) => {
  const direct = String(item?.label || "").trim();
  if (direct) return direct;
  const city = titleCase(item?.city);
  const county = titleCase(item?.county);
  const state = String(item?.state || "").trim().toUpperCase();
  const country = String(item?.country || "").trim().toUpperCase();

  if (city && state) return `${city}, ${state}`;
  if (city && country) return `${city}, ${country}`;
  if (county && state) return `${county} County, ${state}`;
  if (state && country) return `${state}, ${country}`;
  return city || county || state || country;
};

const WelcomeHomePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { appConfig } = useAppConfig();
  const actorId = user?.id || user?.userId || "";
  const {
    workspaces,
    selectedWorkspace,
    selectedWorkspaceUse,
    isOrganizationWorkspace,
    isHiringWorkspace,
    loading: workspacesLoading,
  } = useWorkspace();
  const [keyword, setKeyword] = useState("");
  const [location, setLocation] = useState("");
  const [popularSearchOptions, setPopularSearchOptions] = useState([]);
  const [popularLocationOptions, setPopularLocationOptions] = useState([]);
  const [professionalRoleOptions, setProfessionalRoleOptions] = useState([]);
  const [locationApiSuggestions, setLocationApiSuggestions] = useState([]);
  const [locationApiReady, setLocationApiReady] = useState(false);
  const [searchRan, setSearchRan] = useState(false);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [resultsError, setResultsError] = useState("");
  const [resultsPage, setResultsPage] = useState(1);
  const [resultsTotal, setResultsTotal] = useState(0);
  const [talentResults, setTalentResults] = useState([]);
  const [jobResults, setJobResults] = useState([]);
  const [selectedResult, setSelectedResult] = useState(null);
  const [selectedResultType, setSelectedResultType] = useState("");
  const [selectedResultDetails, setSelectedResultDetails] = useState(null);
  const [resultDetailsLoading, setResultDetailsLoading] = useState(false);
  const [resultDetailsError, setResultDetailsError] = useState("");
  const locationAutocompleteServiceRef = useRef(null);
  const locationFetchSeqRef = useRef(0);
  const googleMapsApiKey =
    appConfig?.googleMapsApiKey ||
    appConfig?.googleMapsApiMatrixKey ||
    process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
    "";

  const hasLinkedWorkspace = workspaces.length > 0;
  const defaultWorkspace = selectedWorkspace || workspaces[0] || null;
  const isHiringContext = isHiringWorkspace || isOrganizationWorkspace || selectedWorkspaceUse === "HIRING";
  const needsOrganizationLink =
    String(defaultWorkspace?.accountType || "").toUpperCase() === "ORGANIZATION" &&
    !defaultWorkspace?.organizationId;
  const copy = useMemo(
    () => homeCopyFor({ isHiringWorkspace: isHiringContext, isOrganizationWorkspace }),
    [isHiringContext, isOrganizationWorkspace],
  );
  const promoCards = useMemo(
    () => promoCardsFor({ isHiringWorkspace: isHiringContext, isOrganizationWorkspace, navigate }),
    [isHiringContext, isOrganizationWorkspace, navigate],
  );
  const quickActions = useMemo(
    () => quickActionsFor({ isHiringWorkspace: isHiringContext, isOrganizationWorkspace }),
    [isHiringContext, isOrganizationWorkspace],
  );
  const keywordSuggestionOptions = useMemo(
    () => {
      const roleMatches = roleOptionsFor(professionalRoleOptions, keyword);
      if (roleMatches.length) return roleMatches;
      const fallbackOptions = popularSearchOptions.length
        ? popularSearchOptions
        : searchSuggestionsFor({ isHiringWorkspace: isHiringContext, isOrganizationWorkspace });
      const normalizedKeyword = keyword.trim().toLowerCase();
      if (!normalizedKeyword) return fallbackOptions.slice(0, 8);
      return fallbackOptions
        .filter((option) => option.value.toLowerCase().includes(normalizedKeyword))
        .slice(0, 8);
    },
    [isHiringContext, isOrganizationWorkspace, keyword, popularSearchOptions, professionalRoleOptions],
  );
  const popularSearchValues = useMemo(
    () => popularSearchOptions.map((item) => item.value).filter(Boolean),
    [popularSearchOptions],
  );
  const popularLocationValues = useMemo(
    () => popularLocationOptions.map((item) => item.value).filter(Boolean),
    [popularLocationOptions],
  );
  const locationSuggestions = useMemo(() => {
    const uniqueLocations = uniqueSuggestionValues(popularLocationValues);
    const normalizedLocation = location.trim().toLowerCase();

    if (!normalizedLocation) return uniqueLocations.slice(0, 8);
    if (googleMapsApiKey && normalizedLocation.length >= 2) {
      return locationApiSuggestions.slice(0, 8);
    }

    const matches = uniqueLocations.filter((item) =>
      item.toLowerCase().includes(normalizedLocation),
    );
    return matches.length > 0 ? matches.slice(0, 8) : uniqueLocations.slice(0, 4);
  }, [googleMapsApiKey, location, locationApiSuggestions, popularLocationValues]);
  const visibleResults = isHiringContext ? talentResults : jobResults;
  const shouldShowResults = searchRan || resultsLoading || resultsError;

  useEffect(() => {
    let alive = true;
    setLocationApiReady(false);
    locationAutocompleteServiceRef.current = null;

    ensureGoogleMapsPlacesLoaded(googleMapsApiKey)
      .then((ok) => {
        if (!alive || !ok || !window.google?.maps?.places) return;
        locationAutocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
        setLocationApiReady(true);
      })
      .catch(() => {
        if (!alive) return;
        setLocationApiReady(false);
      });

    return () => {
      alive = false;
    };
  }, [googleMapsApiKey]);

  useEffect(() => {
    const value = location.trim();
    const service = locationAutocompleteServiceRef.current;

    if (!googleMapsApiKey || !locationApiReady || !service || value.length < 2) {
      locationFetchSeqRef.current += 1;
      setLocationApiSuggestions([]);
      return;
    }

    const seq = ++locationFetchSeqRef.current;
    service.getPlacePredictions(
      { input: value, types: ["(regions)"] },
      (predictions) => {
        if (seq !== locationFetchSeqRef.current) return;
        const seen = new Set();
        const next = (Array.isArray(predictions) ? predictions : [])
          .map((item) => String(item?.description || "").trim())
          .filter((item) => {
            const key = item.toLowerCase();
            if (!key || seen.has(key)) return false;
            seen.add(key);
            return true;
          });
        setLocationApiSuggestions(next);
      },
    );
  }, [googleMapsApiKey, location, locationApiReady]);

  useEffect(() => {
    let cancelled = false;

    const preloadSuggestions = async () => {
      try {
        const [popularSearches, popularLocations] = await Promise.all([
          olreadiaService.appStart.popularSearches({ actorId }),
          olreadiaService.appStart.popularLocations({ actorId }),
        ]);
        if (cancelled) return;

        setPopularSearchOptions(
          uniqueSuggestionValues(
            popularSearches
              .filter((item) => item?.active !== false)
              .map(popularSearchLabel),
          ).map((value) => ({ value, label: value })),
        );
        setPopularLocationOptions(
          uniqueSuggestionValues(
            popularLocations
              .filter((item) => item?.active !== false)
              .map(popularLocationLabel),
          ).map((value) => ({ value, label: value })),
        );
      } catch {
        if (!cancelled) {
          setPopularSearchOptions([]);
          setPopularLocationOptions([]);
        }
      }
    };

    preloadSuggestions();
    return () => {
      cancelled = true;
    };
  }, [actorId]);

  useEffect(() => {
    let cancelled = false;
    const resultId = selectedResult?.id;

    const loadResultDetails = async () => {
      if (!resultId || !selectedResultType) return;
      try {
        setResultDetailsLoading(true);
        setResultDetailsError("");
        const details = selectedResultType === "talent"
          ? await olreadiaService.professionProfiles.get(resultId)
          : await olreadiaService.jobOpportunities.get(resultId, actorId);
        if (!cancelled) setSelectedResultDetails(details);
      } catch (error) {
        if (!cancelled) setResultDetailsError(error?.message || "Could not load full details.");
      } finally {
        if (!cancelled) setResultDetailsLoading(false);
      }
    };

    loadResultDetails();
    return () => {
      cancelled = true;
    };
  }, [actorId, selectedResult?.id, selectedResultType]);

  useEffect(() => {
    let cancelled = false;

    const loadProfessionalRoles = async () => {
      if (!actorId) {
        setProfessionalRoleOptions([]);
        return;
      }
      try {
        const roles = await olreadiaService.professionRoles.list(actorId);
        if (!cancelled) setProfessionalRoleOptions(roles || []);
      } catch {
        if (!cancelled) setProfessionalRoleOptions([]);
      }
    };

    loadProfessionalRoles();
    return () => {
      cancelled = true;
    };
  }, [actorId]);

  const loadHomeResults = async (page = 1, totalCap = null) => {
    try {
      setResultsLoading(true);
      setResultsError("");
      setResultsPage(page);
      const search = [keyword.trim(), location.trim()].filter(Boolean).join(" ");

      if (isHiringContext) {
        const talentPage = await olreadiaService.professionProfiles.searchTalent({
          actorId,
          query: search,
          page: page - 1,
          size: RESULTS_PAGE_SIZE,
        });
        const talentCards = pageContent(talentPage).map(talentCardFromApi).filter((item) => item.id);
        if (page > 1 && talentCards.length === 0) {
          const cappedTotal = (page - 1) * RESULTS_PAGE_SIZE;
          setResultsTotal(cappedTotal);
          await loadHomeResults(page - 1, cappedTotal);
          return;
        }
        setTalentResults(talentCards);
        setJobResults([]);
        const nextTotal = renderedPageTotal(talentPage, talentCards, page);
        setResultsTotal(totalCap == null ? nextTotal : Math.min(nextTotal, totalCap));
        return;
      }

      const jobsPage = await olreadiaService.jobOpportunities.listRecent({
        actorId,
        search,
        page: page - 1,
        size: RESULTS_PAGE_SIZE,
      });
      const jobCards = pageContent(jobsPage).map(jobCardFromApi).filter((item) => item.id);
      if (page > 1 && jobCards.length === 0) {
        const cappedTotal = (page - 1) * RESULTS_PAGE_SIZE;
        setResultsTotal(cappedTotal);
        await loadHomeResults(page - 1, cappedTotal);
        return;
      }
      setJobResults(jobCards);
      setTalentResults([]);
      const nextTotal = renderedPageTotal(jobsPage, jobCards, page);
      setResultsTotal(totalCap == null ? nextTotal : Math.min(nextTotal, totalCap));
    } catch (error) {
      setTalentResults([]);
      setJobResults([]);
      setResultsTotal(0);
      setResultsError(error?.message || "Could not load search results.");
    } finally {
      setResultsLoading(false);
    }
  };

  const runSearch = async () => {
    setSearchRan(true);
    await loadHomeResults(1);
  };

  const handleResultsPageChange = async (page) => {
    setSearchRan(true);
    await loadHomeResults(page);
  };
  const resetResultsForInput = () => {
    setResultsPage(1);
    setResultsTotal(0);
    setSearchRan(false);
    setResultsError("");
  };
  const handleKeywordChange = (value) => {
    setKeyword(value);
    resetResultsForInput();
  };
  const handleLocationChange = (value) => {
    setLocation(value);
    resetResultsForInput();
  };
  const chooseSearchSuggestion = (value) => {
    setKeyword(value);
    resetResultsForInput();
  };
  const chooseLocationSuggestion = (value) => {
    setLocation(value);
    resetResultsForInput();
  };
  const openResultDetails = (item) => {
    if (!item?.id) return;
    setSelectedResult(item);
    setSelectedResultType(isHiringContext ? "talent" : "job");
    setSelectedResultDetails(null);
    setResultDetailsError("");
    setResultDetailsLoading(false);
  };
  const closeResultDetails = () => {
    setSelectedResult(null);
    setSelectedResultType("");
    setSelectedResultDetails(null);
    setResultDetailsError("");
    setResultDetailsLoading(false);
  };
  const applyToSelectedJob = (job) => {
    const jobId = job?.id || job?.jobOpportunityId || selectedResultDetails?.id || selectedResultDetails?.jobOpportunityId || selectedResult?.id;
    if (!jobId) return;
    navigate(`${paths.jobApplications}?jobOpportunityId=${encodeURIComponent(jobId)}`);
  };
  const jobDetailsPath = (job) => {
    const jobId =
      job?.id ||
      job?.jobOpportunityId ||
      job?.raw?.id ||
      job?.raw?.jobOpportunityId ||
      selectedResultDetails?.id ||
      selectedResultDetails?.jobOpportunityId ||
      selectedResult?.id;
    return jobId ? paths.jobOpportunityDetails(encodeURIComponent(jobId)) : "";
  };
  const openJobDetailsPage = (job) => {
    const url = jobDetailsPath(job);
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };
  const shareJobDetails = async (job) => {
    const path = jobDetailsPath(job);
    if (!path) {
      message.warning("This job is missing a shareable details link.");
      return;
    }
    const url = `${window.location.origin}${path}`;
    try {
      if (navigator.share) {
        await navigator.share({
          title: job?.title || selectedResult?.title || "Olreadia job opportunity",
          url,
        });
        return;
      }
      await navigator.clipboard.writeText(url);
      message.success("Job details link copied.");
    } catch (error) {
      if (error?.name !== "AbortError") {
        message.error(error?.message || "Unable to share this job right now.");
      }
    }
  };
  const openSelectedTalentProfile = (profile) => {
    const profileId =
      profile?.professionProfileId ||
      profile?.profileId ||
      profile?.id ||
      selectedResultDetails?.professionProfileId ||
      selectedResultDetails?.profileId ||
      selectedResultDetails?.id ||
      selectedResult?.id;
    if (!profileId) return;
    navigate(paths.professionalProfileDetails(profileId));
  };
  const drawerIsTalent = selectedResultType === "talent";
  const selectedTalentProfile = drawerIsTalent && selectedResult
    ? selectedResultDetails || {
      professionProfileId: selectedResult.id,
      firstName: selectedResult.title,
      profileName: selectedResult.organization,
      summary: selectedResult.summary,
      isVerified: selectedResult.badge === "Identity verified",
      isBackgroundChecked: selectedResult.badge === "Background checked",
      locations: selectedResult.location ? [{ city: selectedResult.location }] : [],
      skills: selectedResult.skills,
    }
    : null;
  const selectedJob = !drawerIsTalent && selectedResult
    ? selectedResultDetails || selectedResult.raw || {
      id: selectedResult.id,
      title: selectedResult.title,
      status: selectedResult.badge || "OPEN",
      locationType: selectedResult.location,
      workSchedules: selectedResult.type ? [selectedResult.type] : [],
      skills: selectedResult.skills,
      description: selectedResult.summary,
    }
    : null;

  return (
    <div className="project-page">
      {workspacesLoading || !hasLinkedWorkspace ? (
        <div className="d-flex justify-content-between align-items-center mb-3 mt-3 gap-3 flex-wrap">
          <WidgetSectionTitle
            icon="home"
            title={`Welcome back, ${userName(user)}`}
            subtitle={defaultWorkspace ? `${copy.modeLabel} / ${defaultWorkspace.name || "Workspace"}` : "Choose or create a workspace to begin"}
            className="flex-1"
            titleStyle={{ color: OLIVEEB_COLORS.oliveebDarkGreen }}
            subtitleStyle={{ color: OLIVEEB_COLORS.oliveebGreen }}
          />
          <Space wrap>
            <Button icon={<TeamOutlined />} onClick={() => navigate(paths.workspaces)}>
              Workspaces
            </Button>
            {hasLinkedWorkspace ? (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate(copy.createPath)}>
                {copy.createLabel}
              </Button>
            ) : null}
          </Space>
        </div>
      ) : null}

      {workspacesLoading ? (
        <Card style={{ borderRadius: 8, borderColor: OLIVEEB_COLORS.border }}>
          <Skeleton active paragraph={{ rows: 5 }} />
        </Card>
      ) : null}

      {!workspacesLoading && !hasLinkedWorkspace ? (
        <Card
          style={{
            ...heroStyle,
            borderColor: OLIVEEB_COLORS.goldBorder,
            background: `linear-gradient(135deg, #ffffff 0%, ${OLIVEEB_COLORS.goldSoft} 58%, ${OLIVEEB_COLORS.oliveebBlueSoft} 100%)`,
          }}
        >
          <span style={shineStyle} />
          <Space direction="vertical" size={14} style={{ width: "100%" }}>
            <span
              style={{
                display: "inline-grid",
                placeItems: "center",
                width: 54,
                height: 54,
                borderRadius: 8,
                background: OLIVEEB_COLORS.oliveebBlueSoft,
                color: OLIVEEB_COLORS.oliveebBlue,
                fontSize: 24,
              }}
            >
              <TeamOutlined />
            </span>
            <div>
              <Title level={3} style={{ margin: 0, color: OLIVEEB_COLORS.oliveebDarkGreen }}>
                Create your first Olreadia workspace
              </Title>
              <Paragraph style={{ color: OLIVEEB_COLORS.textMuted, marginTop: 8, maxWidth: 760 }}>
                Use a personal workspace for job search, professional profiles, applications,
                interviews, and background checks. Use an organization workspace for hiring,
                job posts, candidate review, and team workflows.
              </Paragraph>
            </div>
            <Button
              type="primary"
              size="large"
              icon={<PlusOutlined />}
              onClick={() => navigate(paths.workspaceCreate())}
              style={{
                width: "fit-content",
                ...actionButtonStyle,
                background: OLIVEEB_COLORS.oliveebGreen,
                borderColor: OLIVEEB_COLORS.oliveebGreen,
              }}
            >
              Create workspace
            </Button>
          </Space>
        </Card>
      ) : null}

      {!workspacesLoading && hasLinkedWorkspace ? (
        <div className="welcome-home-dashboard">
          <main className="welcome-home-main">
            <div className="welcome-home-titlebar">
              <WidgetSectionTitle
                icon="home"
                title={`Welcome back, ${userName(user)}`}
                subtitle={`${copy.modeLabel} / ${defaultWorkspace.name || "Workspace"}`}
                className="flex-1"
                titleStyle={{ color: OLIVEEB_COLORS.oliveebDarkGreen }}
                subtitleStyle={{ color: OLIVEEB_COLORS.oliveebGreen }}
              />
              <Space wrap>
                <Button icon={<TeamOutlined />} onClick={() => navigate(paths.workspaces)}>
                  Workspaces
                </Button>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate(copy.createPath)}>
                  {copy.createLabel}
                </Button>
              </Space>
            </div>

            {needsOrganizationLink ? (
              <Alert
                type="info"
                showIcon
                message={
                  <Text style={{ color: OLIVEEB_COLORS.oliveebBlue, fontWeight: 800 }}>
                    This workspace is not linked to an organization.
                  </Text>
                }
                description={
                  <Space direction="vertical" size={8} align="start">
                    <Text style={{ color: OLIVEEB_COLORS.oliveebBlue }}>
                      Link an organization to unlock business subscriptions, billing, and organization settings.
                    </Text>
                    <Button
                      size="small"
                      type="primary"
                      icon={<LinkOutlined />}
                      onClick={() => navigate(paths.workspaceOrganization(defaultWorkspace.id))}
                      style={{
                        background: OLIVEEB_COLORS.oliveebBlue,
                        borderColor: OLIVEEB_COLORS.oliveebBlue,
                        color: OLIVEEB_COLORS.white,
                        fontWeight: 800,
                      }}
                    >
                      Link organization
                    </Button>
                  </Space>
                }
                style={{
                  textAlign: "left",
                  borderRadius: 8,
                  borderColor: OLIVEEB_COLORS.oliveebBlueSoft,
                  background: "linear-gradient(135deg, #F2F8FF 0%, #FFFFFF 100%)",
                }}
              />
            ) : null}

            <section className="welcome-home-hero">
              <span className="welcome-home-hero__shine" />
              <div className="welcome-home-hero__copy">
                <Tag className="welcome-home-mode-tag">{copy.modeLabel}</Tag>
                <Title level={2}>{copy.title}</Title>
                <Paragraph>{copy.subtitle}</Paragraph>
                <div className="welcome-home-summary-strip">
                  <span>
                    <strong>{isHiringContext ? "Find talent" : "Find roles"}</strong>
                    <small>{isHiringContext ? "Search qualified candidates" : "Search open opportunities"}</small>
                  </span>
                  <span>
                    <strong>{isHiringContext ? "Review pipeline" : "Profile ready"}</strong>
                    <small>{isHiringContext ? "Applications and interviews" : "Keep your details current"}</small>
                  </span>
                  <span>
                    <strong>{isHiringContext ? "Post faster" : "Track progress"}</strong>
                    <small>{isHiringContext ? "Create structured job posts" : "Applications and next steps"}</small>
                  </span>
                </div>
              </div>
              <div
                className="welcome-home-hero__image"
                style={{ backgroundImage: "linear-gradient(180deg, rgba(6, 39, 1, 0.04), rgba(9, 3, 89, 0.16)), url(/home.png)" }}
                aria-label="Olreadia home"
              />
              <Card
                size="small"
                title={<Text strong>{copy.searchTitle}</Text>}
                className="welcome-home-search-card"
              >
                <div className="welcome-home-search-grid">
                  <AutoComplete
                    className="welcome-home-search-field welcome-home-search-field--keyword"
                    allowClear={false}
                    value={keyword}
                    onChange={handleKeywordChange}
                    onSelect={chooseSearchSuggestion}
                    options={keywordSuggestionOptions.filter((option) =>
                      !keyword.trim() ||
                      option.value.toLowerCase().includes(keyword.trim().toLowerCase()),
                    )}
                    filterOption={false}
                    popupClassName="welcome-home-search-dropdown"
                    style={{ width: "100%", ...fieldStyle }}
                  >
                    <Input
                      size="large"
                      onPressEnter={runSearch}
                      placeholder={copy.keywordPlaceholder}
                      prefix={<SearchOutlined />}
                      suffix={
                        keyword ? (
                          <Button
                            type="text"
                            size="small"
                            className="welcome-home-search-clear"
                            icon={<CloseOutlined />}
                            onClick={() => handleKeywordChange("")}
                            aria-label="Clear search"
                          />
                        ) : null
                      }
                    />
                  </AutoComplete>
                  <AutoComplete
                    className="welcome-home-search-field welcome-home-search-field--location"
                    allowClear={false}
                    value={location}
                    onChange={handleLocationChange}
                    onSelect={chooseLocationSuggestion}
                    options={locationSuggestions.map((item) => ({ value: item, label: item }))}
                    filterOption={false}
                    popupClassName="welcome-home-search-dropdown"
                    style={{ width: "100%" }}
                  >
                    <Input
                      size="large"
                      onPressEnter={runSearch}
                      placeholder={copy.locationPlaceholder}
                      prefix={<EnvironmentOutlined />}
                      suffix={
                        location ? (
                          <Button
                            type="text"
                            size="small"
                            className="welcome-home-search-clear"
                            icon={<CloseOutlined />}
                            onClick={() => handleLocationChange("")}
                            aria-label="Clear location"
                          />
                        ) : null
                      }
                    />
                  </AutoComplete>
                  <Button className="welcome-home-search-submit" type="primary" size="large" icon={<SearchOutlined />} onClick={runSearch} loading={resultsLoading}>
                    {copy.primarySearchLabel}
                  </Button>
                </div>
                <div className="welcome-home-suggestion-block">
                  <Text className="welcome-home-suggestion-label">Popular searches</Text>
                  <div className="welcome-home-chip-row">
                    {popularSearchValues.slice(0, 8).map((item) => (
                      <button
                        type="button"
                        key={item}
                        className={`welcome-home-suggestion-chip${keyword === item ? " is-active" : ""}`}
                        onClick={() => chooseSearchSuggestion(item)}
                      >
                        {item}
                      </button>
                    ))}
                  </div>

                  <Text className="welcome-home-suggestion-label is-spaced">Popular locations</Text>
                  <div className="welcome-home-chip-row">
                    {popularLocationValues.slice(0, 8).map((item) => (
                      <button
                        type="button"
                        key={item}
                        className={`welcome-home-suggestion-chip is-location${location === item ? " is-active" : ""}`}
                        onClick={() => chooseLocationSuggestion(item)}
                      >
                        {item}
                      </button>
                    ))}
                  </div>
                </div>
              </Card>
              {shouldShowResults ? (
                <div className="welcome-home-results-panel">
                  <div className="welcome-home-results-head">
                    <span>
                      <Text className={`welcome-home-results-title${isHiringContext ? " is-talent" : " is-opportunity"}`}>
                        {isHiringContext ? "Talent search results" : "Opportunity search results"}
                      </Text>
                    </span>
                    {!resultsError ? (
                      <span className="welcome-home-result-count">
                        {resultsLoading
                          ? "Loading"
                          : `${resultsTotal || visibleResults.length} ${(resultsTotal || visibleResults.length) === 1 ? "result" : "results"}`}
                      </span>
                    ) : null}
                  </div>

                  <div className="welcome-home-result-list">
                    {resultsError ? (
                      <div className="welcome-home-empty-state">
                        <Text className="welcome-home-empty-title">Unable to load results</Text>
                        <Text className="welcome-home-empty-text">{resultsError}</Text>
                      </div>
                    ) : visibleResults.length > 0 ? (
                      isHiringContext ? (
                        visibleResults.map((item) => (
                          <article
                            className="welcome-home-result-card"
                            key={`talent-${item.id}`}
                            onClick={() => openResultDetails(item)}
                          >
                            <div>
                              <div className="welcome-home-result-meta">
                                {item.location ? (
                                  <span className="welcome-home-result-pill">
                                    <EnvironmentOutlined /> {item.location}
                                  </span>
                                ) : null}
                                {item.type ? <span className="welcome-home-result-pill">{item.type}</span> : null}
                                {item.badge ? <span className="welcome-home-result-pill">{item.badge}</span> : null}
                              </div>
                              <Text className="welcome-home-result-title">{item.title}</Text>
                              <Text className="welcome-home-result-subtitle">{item.organization}</Text>
                              {item.summary ? <Text className="welcome-home-result-summary">{item.summary}</Text> : null}
                              {item.skills.length ? (
                                <div className="welcome-home-result-skills">
                                  {item.skills.slice(0, 6).map((skill) => (
                                    <span className="welcome-home-result-skill" key={skill}>
                                      {skill}
                                    </span>
                                  ))}
                                </div>
                              ) : null}
                            </div>

                            <Button
                              icon={<FileDoneOutlined />}
                              onClick={(event) => {
                                event.stopPropagation();
                                openResultDetails(item);
                              }}
                            >
                              View details
                            </Button>
                          </article>
                        ))
                      ) : (
                        visibleResults.map((item) => (
                          <JobCard
                            key={`job-${item.id}`}
                            job={item.raw || item}
                            onOpen={() => openResultDetails(item)}
                            onApply={() => applyToSelectedJob(item.raw || item)}
                            onShare={() => shareJobDetails(item.raw || item)}
                          />
                        ))
                      )
                    ) : resultsLoading ? (
                      <div className="welcome-home-empty-state">
                        <Text className="welcome-home-empty-title">Loading results...</Text>
                        <Text className="welcome-home-empty-text">Fetching the latest records from Olreadia.</Text>
                      </div>
                    ) : (
                      <div className="welcome-home-empty-state">
                        <Text className="welcome-home-empty-title">No matches yet</Text>
                        <Text className="welcome-home-empty-text">Try a broader keyword or choose one of the popular searches above.</Text>
                      </div>
                    )}
                  </div>

                  {!resultsError && visibleResults.length > 0 && resultsTotal > RESULTS_PAGE_SIZE ? (
                    <div className="welcome-home-results-pagination">
                      <Pagination
                        size="small"
                        current={resultsPage}
                        pageSize={RESULTS_PAGE_SIZE}
                        total={resultsTotal}
                        showSizeChanger={false}
                        disabled={resultsLoading}
                        onChange={handleResultsPageChange}
                      />
                    </div>
                  ) : null}
                </div>
              ) : null}
            </section>

            <section className="welcome-home-section">
              <div className="welcome-home-section__header">
                <div>
                  <Text strong>Quick actions</Text>
                  <Paragraph>Open the workflows you use most often.</Paragraph>
                </div>
                <Button type="link" onClick={() => navigate(copy.secondaryPath)}>
                  {copy.secondaryLabel} <ArrowRightOutlined />
                </Button>
              </div>
              <div className="welcome-home-quick-grid">
                {quickActions.map((action) => (
                  <button key={action.label} type="button" className="welcome-home-action" onClick={() => navigate(action.to)}>
                    <span className="welcome-home-action__icon">{action.icon}</span>
                    <strong>{action.label}</strong>
                    <ArrowRightOutlined />
                  </button>
                ))}
              </div>
            </section>

            <section className="welcome-home-section">
              <div className="welcome-home-section__header">
                <div>
                  <Text strong>Recommended next steps</Text>
                  <Paragraph>{isHiringContext ? "Keep your hiring pipeline moving." : "Stay ready for stronger applications."}</Paragraph>
                </div>
              </div>
              <div className="welcome-home-next-grid">
                {promoCards.map((promo) => (
                  <Card key={promo.title} hoverable className="welcome-home-next-card" onClick={promo.onClick}>
                    <div className="welcome-home-next-card__image" style={{ backgroundImage: `linear-gradient(180deg, rgba(9, 3, 89, 0.08), rgba(14, 86, 4, 0.24)), url(${promo.image})` }} />
                    <div className="welcome-home-next-card__body">
                      <span className="welcome-home-next-card__icon">{promo.icon}</span>
                      <div>
                        <Title level={5}>{promo.title}</Title>
                        <Paragraph>{promo.text}</Paragraph>
                      </div>
                      <Button type="link" onClick={(event) => {
                        event.stopPropagation();
                        promo.onClick();
                      }}>
                        {promo.action} <ArrowRightOutlined />
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            </section>
          </main>

          <aside className="welcome-home-rail">
            <SidePromotionSection showControls={false} />
          </aside>
        </div>
      ) : null}

      <JobDetailsDrawer
        job={selectedJob}
        open={Boolean(selectedResult && selectedResultType === "job")}
        onClose={closeResultDetails}
        onApply={applyToSelectedJob}
        onOpenDetails={openJobDetailsPage}
        onShare={shareJobDetails}
      />
      <TalentProfileDrawer
        actorId={actorId}
        detailsError={resultDetailsError}
        detailsLoading={resultDetailsLoading}
        onClose={closeResultDetails}
        onOpenProfileDetails={openSelectedTalentProfile}
        open={Boolean(selectedResult && selectedResultType === "talent")}
        selectedProfile={selectedTalentProfile}
        selectedProfileDetails={selectedResultDetails}
      />
    </div>
  );
};

export default WelcomeHomePage;
